import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Home, LayoutGrid, List } from 'lucide-react';
import { CustomBreadcrumb, CustomInput, CustomButton } from '@/shared/components/atoms';
import { CustomCommonScreenHeader } from '@/shared/components/molecules';
import { useToastStore } from '@/shared/stores/toastStore';
import { useAuthStore } from '@/shared/stores/authStore';
import { departmentApi } from '../api/departmentApi';

const addSubDepartmentSchema = z.object({
    name: z.string().min(2, 'Sub-Department name must be at least 2 characters'),
});

type AddSubDepartmentFormData = z.infer<typeof addSubDepartmentSchema>;

export const AddSubDepartment = () => {
    const navigate = useNavigate();
    const { departmentId } = useParams<{ departmentId: string }>();
    const [isLoading, setIsLoading] = useState(false);
    const { showToast } = useToastStore();

    const { user } = useAuthStore();
    const schoolSlug = user?.school?.slug || '';

    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<AddSubDepartmentFormData>({
        resolver: zodResolver(addSubDepartmentSchema)
    });

    const onSubmit = async (data: AddSubDepartmentFormData) => {
        if (!departmentId) return;
        setIsLoading(true);
        try {
            const response = await departmentApi.createSubDepartment(schoolSlug, {
                name: data.name,
                department_id: departmentId
            });

            if (response.success !== false) {
                showToast('Sub-Department added successfully', 'success');
                navigate(`/${schoolSlug}/school-admin/departments/${departmentId}/sub-departments`);
            } else {
                showToast(response.error || 'Failed to add sub-department', 'error');
            }
        } catch (error) {
            showToast('An unexpected error occurred', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div style={{ padding: '1rem 1.5rem' }}>
            <CustomBreadcrumb
                items={[
                    { label: 'Home', path: '/', icon: <Home size={14} /> },
                    { label: 'Departments', path: `/${schoolSlug}/school-admin/departments`, icon: <LayoutGrid size={14} /> },
                    { label: 'Sub-Departments', path: `/${schoolSlug}/school-admin/departments/${departmentId}/sub-departments`, icon: <List size={14} /> },
                    { label: 'Add Sub-Department' }
                ]}
            />

            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem' }}>
                <CustomCommonScreenHeader
                    title="Add Sub-Department"
                    subtitle="Create a new sub-department"
                    icon={<List size={24} />}
                />
            </div>

            <div style={{
                maxWidth: '700px',
                margin: '2rem auto',
                background: 'var(--card-bg)',
                borderRadius: '16px',
                border: '1px solid var(--border-color)',
                padding: '2.5rem',
                boxShadow: 'var(--card-shadow)'
            }}>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.5rem' }}>
                        <div>
                            <CustomInput
                                label="Sub-Department Name"
                                placeholder="Enter sub-department name (e.g. Teaching Staff)"
                                leftIcon={<List size={18} />}
                                error={errors.name?.message}
                                {...register('name')}
                                required
                            />
                        </div>

                        <div style={{
                            marginTop: '2rem',
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '1rem',
                            borderTop: '1px solid var(--border-color)',
                            paddingTop: '2rem'
                        }}>
                            <CustomButton
                                variant="outline"
                                color="secondary"
                                onClick={() => navigate(-1)}
                                disabled={isLoading}
                            >
                                Cancel
                            </CustomButton>
                            <CustomButton
                                type="submit"
                                variant="filled"
                                color="primary"
                                loading={isLoading}
                            >
                                Add Sub-Department
                            </CustomButton>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};
